import { Typography } from '@/components/atoms';
import { LayersIcon } from '@/components/atoms/icons';
import { useUIColors } from '@/core/hooks';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import type {
  GuestDetailsLayout,
  GuestDetailsTypeScale,
} from '../constants/guestLayout';
import type { GuestLabels } from '../constants/getGuestLabels';
import type { GuestUnitDetailsModel } from '../types/guest.types';
import { GuestAmenitiesGrid } from './GuestAmenitiesGrid';

export interface GuestUnitAboutSectionsProps {
  readonly unit: GuestUnitDetailsModel;
  readonly layout: GuestDetailsLayout;
  readonly typeScale: GuestDetailsTypeScale;
  readonly labels: GuestLabels;
  /** ADHA home: omit Unit Type tags from About Property. */
  readonly hideUnitType?: boolean;
}

function UnitsCountSection({
  unitsCount,
  layout,
  typeScale,
  label,
}: Readonly<{
  unitsCount: number;
  layout: GuestDetailsLayout;
  typeScale: GuestDetailsTypeScale;
  label: string;
}>) {
  const colors = useUIColors();
  const sectionStyle = layout.isPhone
    ? [styles.aboutSection, styles.aboutSectionPhone]
    : [styles.aboutSection];
  const rowStyle = layout.isPhone
    ? [styles.aboutRow, styles.aboutRowPhone, { backgroundColor: colors.backgroundLevel2 }]
    : [styles.aboutRow, { backgroundColor: colors.backgroundLevel2 }];

  return (
    <View style={sectionStyle}>
      <View style={rowStyle}>
        <Typography
          variant={typeScale.aboutHeading}
          color="primary"
          style={styles.aboutSectionLabel}
        >
          {label}
        </Typography>
        <View style={styles.aboutRowValue}>
          <LayersIcon size={18} color={colors.fontPrimary} />
          <Typography
            variant={typeScale.aboutValue}
            color="secondary"
            style={styles.unitsCountText}
          >
            {String(unitsCount)}
          </Typography>
        </View>
      </View>
    </View>
  );
}

function TypeTagsSection({
  typeTags,
  layout,
  typeScale,
  label,
}: Readonly<{
  typeTags: GuestUnitDetailsModel['typeTags'];
  layout: GuestDetailsLayout;
  typeScale: GuestDetailsTypeScale;
  label: string;
}>) {
  const colors = useUIColors();
  const sectionStyle = layout.isPhone
    ? [
        styles.aboutSection,
        styles.aboutBlock,
        styles.aboutSectionPhone,
        styles.aboutBlockPhone,
      ]
    : [styles.aboutSection, styles.aboutBlock];

  return (
    <View style={sectionStyle}>
      <Typography
        variant={typeScale.aboutHeading}
        color="primary"
        style={styles.aboutSectionLabel}
      >
        {label}
      </Typography>
      <View style={styles.tagsRow}>
        {typeTags.map((tag) => {
          const tagStyle = layout.isCompact
            ? [styles.tag, styles.tagCompact, { backgroundColor: colors.propertyTagBg }]
            : [styles.tag, { backgroundColor: colors.propertyTagBg }];
          return (
            <View key={tag.id} style={tagStyle}>
              <Typography
                variant={typeScale.aboutTag}
                color="primary"
                numberOfLines={1}
                style={styles.tagText}
              >
                {tag.label}
              </Typography>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function AmenitiesSection({
  amenities,
  layout,
  typeScale,
  label,
}: Readonly<{
  amenities: ReadonlyArray<string>;
  layout: GuestDetailsLayout;
  typeScale: GuestDetailsTypeScale;
  label: string;
}>) {
  const sectionStyle = layout.isPhone
    ? [
        styles.aboutSection,
        styles.aboutBlock,
        styles.aboutSectionPhone,
        styles.aboutBlockPhone,
      ]
    : [styles.aboutSection, styles.aboutBlock];

  return (
    <View style={sectionStyle}>
      <Typography
        variant={typeScale.aboutHeading}
        color="primary"
        style={styles.aboutSectionLabel}
      >
        {label}
      </Typography>
      <GuestAmenitiesGrid
        amenities={amenities}
        isCompact={layout.isCompact}
        isPhone={layout.isPhone}
        labelVariant={typeScale.amenityLabel}
      />
    </View>
  );
}

/**
 * True when About would show at least one section (units / types / amenities).
 */
export function hasGuestAboutContent(
  unit: GuestUnitDetailsModel,
  options?: Readonly<{ hideUnitType?: boolean }>,
): boolean {
  const showUnitType = !options?.hideUnitType && unit.typeTags.length > 0;
  return (
    typeof unit.unitsCount === 'number' ||
    showUnitType ||
    (unit.amenities?.length ?? 0) > 0
  );
}

/**
 * About accordion body sections.
 * Memoized — callers embedding this in a large single-component screen (e.g.
 * ProjectDetailsScreenAdhaMobile) re-render often for reasons unrelated to
 * this content; React.memo skips redoing this work when `unit`/`layout`/
 * `typeScale`/`labels` haven't actually changed.
 */
export const GuestUnitAboutSections = React.memo(function GuestUnitAboutSections({
  unit,
  layout,
  typeScale,
  labels,
  hideUnitType = false,
}: Readonly<GuestUnitAboutSectionsProps>) {
  const amenities = unit.amenities ?? [];
  const showUnitType = !hideUnitType && unit.typeTags.length > 0;

  return (
    <View style={styles.aboutContent}>
      {typeof unit.unitsCount === 'number' ? (
        <UnitsCountSection
          unitsCount={unit.unitsCount}
          layout={layout}
          typeScale={typeScale}
          label={labels.numberOfUnits}
        />
      ) : null}
      {showUnitType ? (
        <TypeTagsSection
          typeTags={unit.typeTags}
          layout={layout}
          typeScale={typeScale}
          label={labels.unitType}
        />
      ) : null}
      {amenities.length > 0 ? (
        <AmenitiesSection
          amenities={amenities}
          layout={layout}
          typeScale={typeScale}
          label={labels.amenities}
        />
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  aboutSectionLabel: {
    color: '#000000',
    letterSpacing: 0,
  },
  aboutContent: {
    gap: 0,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  aboutSection: {
    paddingVertical: 16,
  },
  aboutSectionPhone: {
    paddingVertical: 12,
  },
  aboutRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: 12,
    borderRadius: 8,
  },
  aboutRowPhone: {
    padding: 10,
  },
  aboutRowValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  unitsCountText: {
    letterSpacing: 0,
  },
  aboutBlock: {
    gap: 16,
    width: '100%',
    minWidth: 0,
  },
  aboutBlockPhone: {
    gap: 12,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    width: '100%',
    minWidth: 0,
  },
  tag: {
    minWidth: 173,
    minHeight: 29,
    maxWidth: '100%',
    flexShrink: 1,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  tagCompact: {
    minWidth: 0,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  tagText: {
    letterSpacing: 0,
    flexShrink: 1,
  },
});

export default GuestUnitAboutSections;
